import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { Page, PageHeader, Card, Segmented, StatusBadge, Label11, PrivacyNote, Empty, Loading, int, plural, ago } from "@/components/common";
import { TextScramble } from "@/components/Motion";
import { EngineWithLogo } from "@/components/ProviderLogo";
import { investigatePath } from "@/components/InvestigateTimeline";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight } from "lucide-react";

// Investigate (docs/INVESTIGATE_PLAN.md part 3): the company's sessions that had a problem in the last 30 days, the
// latest first (GET /investigate/sessions, read from counters on each session). Each opens its timeline.
const DAYS = 30;
const PROBLEMS = [["any", "Any problem"], ["tool_failures", "Tool failures"], ["errors", "Errors"], ["refused", "Refused calls"],
                  ["model_errors", "Failed model calls"], ["integrity", "Integrity"]];
const NONE = { any: "a problem", tool_failures: "a failed tool call", errors: "an error", refused: "a refused model call",
               model_errors: "a failed model call", integrity: "an integrity problem" };
// a session's problems, in words: [counter on the session, one, many]
const COUNTS = [["ev_tool_failures", "tool call failed", "tool calls failed"], ["ev_errors", "error", "errors"],
                ["ev_refused", "model call refused", "model calls refused"], ["ev_model_errors", "model call failed", "model calls failed"]];
// one row per session: who and where on the left, what went wrong in the middle, when on the right (stacked on a phone)
const ROW = "grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)_112px] gap-x-6 gap-y-2.5 md:items-center px-5";

const Count = ({ n, children, tone = "text-gr-danger" }) => (
  <span className="inline-flex items-baseline gap-1.5 rounded-md bg-gr-surface-2 px-2 py-0.5 text-sm">
    <span className={cn("font-mono font-semibold tabular-nums", tone)}>{int(n)}</span><span>{children}</span>
  </span>
);

// what went wrong: only what happened, as chips, and the last integrity check (investigate.py) when one ran
function Problems({ s }) {
  return (
    <span className="flex flex-wrap gap-1.5" data-testid={`investigate-problems-${s.short_id}`}>
      {COUNTS.filter(([k]) => s[k] > 0).map(([k, one, many]) => <Count key={k} n={s[k]}>{s[k] === 1 ? one : many}</Count>)}
      {s.integrity_ok != null && (s.ev_integrity > 0
        ? <Count n={s.ev_integrity}>{s.ev_integrity === 1 ? "integrity problem" : "integrity problems"}</Count>
        : (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-gr-surface-2 px-2 py-0.5 text-sm text-muted-foreground">
            <Check className="w-3.5 h-3.5 text-gr-success" aria-hidden="true" />Log checks passed
          </span>
        ))}
    </span>
  );
}

export default function Investigate() {
  const [problem, setProblem] = useState("any");
  // a page at a time (100), the latest first; "Show more" goes on from next_offset
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["investigate-sessions", problem], refetchInterval: 30000,
    queryFn: async ({ pageParam }) =>
      (await api.get("/investigate/sessions", { params: { days: DAYS, problem, offset: pageParam, limit: 100 } })).data,
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
  });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const names = Object.fromEntries((team?.members || []).map((m) => [m.id, m.name || m.email]));
  const sessions = (data?.pages || []).flatMap((p) => p.sessions);
  const total = data?.pages?.[0]?.total ?? sessions.length;
  const status = isLoading ? undefined : sessions.length
    ? `${plural(total, "session")} had ${problem === "any" ? "problems" : NONE[problem].replace(/^an? /, "") + "s"} in the last ${DAYS} days.`
    : `No session had ${NONE[problem]} in the last ${DAYS} days.`;
  return (
    <Page className="gap-5" data-testid="owner-investigate">
      <PageHeader eyebrow={`Investigate · Last ${DAYS} days`} title="Sessions where something went wrong." status={status} />
      <Segmented label="Problem" className="self-start" options={PROBLEMS} value={problem} onChange={setProblem} testid="investigate-problem" />
      {isLoading ? <Loading /> : error ? (
        <Card className="px-5 py-6 text-muted-foreground">{apiErr(error)}</Card>
      ) : !sessions.length ? (
        <Empty testid="investigate-empty">
          No session had {NONE[problem]} in the last {DAYS} days. A session shows up here when a tool or model call fails,
          a call is refused, an error stops a turn, or a check finds its log doesn't match its other records.
        </Empty>
      ) : (
        <div className="bg-card rounded-[10px] overflow-hidden" data-testid="investigate-sessions">
          <div className={cn(ROW, "hidden md:grid py-2.5 border-b")}>
            <Label11>Session</Label11><Label11>What went wrong</Label11><Label11 className="text-right">Last problem</Label11>
          </div>
          {sessions.map((s) => (
            <Link key={s.id} to={investigatePath("owner", s.id)} data-testid={`investigate-row-${s.short_id}`}
              className={cn(ROW, "group py-3.5 border-b last:border-b-0 transition-colors hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring")}>
              <span className="flex flex-col gap-1 min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-[15px] tracking-[0.02em] font-semibold tabular-nums"><TextScramble>{s.short_id}</TextScramble></span>
                  {s.review_ready ? <StatusBadge label="In review" tone="warning" /> : <StatusBadge status={s.status} live />}
                </span>
                <span className="text-sm truncate" title={names[s.coder_id]}>{names[s.coder_id] || "A former member"}</span>
                <span className="flex items-center gap-1.5 min-w-0 text-sm text-muted-foreground">
                  <EngineWithLogo engine={s.engine} className="gap-1.5 shrink-0 [&_svg]:w-3.5 [&_svg]:h-3.5" />
                  <span aria-hidden="true">·</span><span className="truncate font-mono text-[12px]">{s.model}</span>
                </span>
              </span>
              <Problems s={s} />
              <span className="flex items-center md:justify-end gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                {ago(s.last_problem_at)}
                <ChevronRight className="w-4 h-4 transition-colors group-hover:text-foreground" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      )}
      {hasNextPage && (
        <Button variant="outline" className="self-start" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
          data-testid="investigate-more">
          {isFetchingNextPage ? "Loading…" : `Show more (${int(total - sessions.length)} left)`}
        </Button>
      )}
      <PrivacyNote>
        Investigate shows what happened in each session: tool and model call outcomes, errors, files and commits. It never
        shows what was said, what the agent replied, or what a command contained.
      </PrivacyNote>
    </Page>
  );
}
